import { Group } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateGroupArgs,
    getDefaultGroupCreateArgs,
    UpdateGroupArgs,
} from "@nova/serializer/src/client/validators/cubes/groups"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import {
    createFractionalIndexBetween,
    END,
    START,
} from "@nova/serializer/src/client/util/fractional_indexing"
import { selectSelectedLayers, selectSingleLayer } from "panels/content/side-panel/slice.new"
import { getSidePanelLayerTypeToUpdateFunctionMap } from "plugins/plugin-registry"
import { getLocalStorageValue, setLocalStorageValue } from "tools/store/localStorageRegistry"

const {
    create: createGroup,
    update: updateGroup,
    delete: deleteGroup,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    Group, // entity type
    CreateGroupArgs, // create args to send to server
    ["name", "createdBy", "groupType"], // create args required by action
    UpdateGroupArgs, // update args
    { index?: string; parentId?: string; id?: string; isEmpty?: boolean }, // required input context
    void // server context
>("Group")

export const addGroupsToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const optimisticData: Group = {
                ...data,
                id: context?.id || randomUUID(),
                ...getDefaultGroupCreateArgs(store.getState().project.data),
            }

            // Override the index only if it was specified in the create action.
            if (context?.index) optimisticData.index = context.index
            if (context?.parentId) optimisticData.parentId = context.parentId

            createGroup(
                socket,
                {
                    ...data,
                    id: optimisticData.id,
                    index: optimisticData.index,
                    parentId: optimisticData.parentId,
                },
                undefined
            )
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["groups"],
                })
            )

            if (context?.isEmpty) {
                store.dispatch(selectSingleLayer({ id: optimisticData.id }))
                const expandedLayerIds = getLocalStorageValue("sidePanelExpandedLayerIds")
                if (!expandedLayerIds.includes(optimisticData.id)) {
                    setLocalStorageValue("sidePanelExpandedLayerIds", [
                        ...expandedLayerIds,
                        optimisticData.id,
                    ])
                }
                return
            }

            // The selection will always be top-down, so we want to reverse it so we can
            // effectively "add" each layer to the stack from the bottom of the group to the top.
            const state = store.getState()
            const sublayers = [...selectSelectedLayers(state)].reverse()
            const newIndices: string[] = []

            // For each sublayer, re-assign it's parentId and index.
            sublayers.forEach((sublayer, i) => {
                if (!sublayer.type)
                    throw Error(
                        "Tried to make group with selection where at least 1 layer does not have a type (SidePanelLayerType) asssigned to it."
                    )

                newIndices.push(createFractionalIndexBetween(newIndices[i - 1] || START, END))

                const dispatch = store.dispatch
                dispatch(
                    getSidePanelLayerTypeToUpdateFunctionMap()[sublayer.type]({
                        id: sublayer.id,
                        updates: {
                            index: newIndices[i],
                            parentId: optimisticData.id,
                        },
                    })
                )
            })
        })
        .addCase(updateAction, (store, { payload: { id, updates } }, socket) => {
            updateGroup(socket, id, updates)
            store.dispatch(updateEntitySocket({ id, updates, path: ["groups", { id }] }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            deleteGroup(socket, id)
            store.dispatch(deleteEntitySocket({ path: ["groups", { id }] }))
        })

export { createAction as createGroup, updateAction as updateGroup, deleteAction as deleteGroup }
